import { formatNum, type MoneyCurrency } from './money.js';
import { escapeHtml } from './dom.js';

/**
 * The currency chip — legacy webledger's `currencyChip`, ported whole.
 *
 * A figure in a multi-currency ledger is meaningless without its currency,
 * and legacy's answer is one control used *everywhere* a signed amount
 * appears: the account tree's balances, the register's value and running
 * columns, folder totals, the reports. The point of it being one control is
 * that 469 never appears twice looking the same and meaning different money.
 *
 * Anatomy, kept as legacy has it: a flag above a small code (the two together
 * are one marker, narrow enough to sit in a table column), then the amount,
 * with the currency's own decimal places rather than a fixed two.
 *
 * `data-cur` / `data-amt` carry the raw values. Legacy reads them for its
 * Cmd/Ctrl-click chip tally — pick chips from any page, get a running sum —
 * and they are emitted here so that feature can be added without revisiting
 * every call site. Nothing reads them yet.
 */

const FLAG_SIZE = { width: 20, height: 14 };

const rect = (x: number, y: number, w: number, h: number, fill: string): string =>
  `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${fill}"/>`;
const circle = (cx: number, cy: number, r: number, fill: string): string =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}"/>`;
const line = (x1: number, y1: number, x2: number, y2: number, stroke: string, w: number): string =>
  `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${stroke}" stroke-width="${w}"/>`;

/**
 * Hand-drawn flags for the currencies this ledger actually uses, ported
 * shape-for-shape from legacy's `flagSvg`.
 *
 * Drawn rather than fetched or set in an emoji font, for legacy's reasons and
 * ours: no network dependency in a page that must work offline, no icon font,
 * and identical rendering everywhere — flag emoji are missing entirely on
 * some platforms and render as two letters on others, which is exactly the
 * ambiguity the chip exists to remove.
 *
 * An unknown currency gets a plain grey field. It is a deliberate blank
 * rather than a guess: the code beneath it is the identification, and the
 * flag is the shortcut for the ones you see every day.
 */
export function flagSvg(code: string): string {
  const cur = (code || '').toUpperCase();
  let body: string;

  if (cur === 'HUF') {
    body = rect(0, 0, 20, 14, '#fff') + rect(0, 0, 20, 4.67, '#cd2a3e') + rect(0, 9.33, 20, 4.67, '#436f4d');
  } else if (cur === 'EUR') {
    body = rect(0, 0, 20, 14, '#039');
    for (let i = 0; i < 12; i += 1) {
      const a = (Math.PI / 180) * (i * 30 - 90);
      body += circle(10 + 4 * Math.cos(a), 7 + 4 * Math.sin(a), 0.9, '#fc0');
    }
  } else if (cur === 'USD') {
    body = rect(0, 0, 20, 14, '#fff');
    for (let i = 0; i < 7; i += 1) body += rect(0, i * 2, 20, 1, '#b22234');
    body += rect(0, 0, 8.5, 7.5, '#3c3b6e');
    for (let y = 1; y < 7; y += 2) for (let x = 1; x < 8; x += 2) body += circle(x, y, 0.35, '#fff');
  } else if (cur === 'GBP') {
    body = rect(0, 0, 20, 14, '#012169')
      + line(0, 0, 20, 14, '#fff', 3) + line(20, 0, 0, 14, '#fff', 3)
      + line(0, 0, 20, 14, '#C8102E', 1.3) + line(20, 0, 0, 14, '#C8102E', 1.3)
      + rect(0, 5.5, 20, 3, '#fff') + rect(8.5, 0, 3, 14, '#fff')
      + rect(0, 6, 20, 2, '#C8102E') + rect(9, 0, 2, 14, '#C8102E');
  } else if (cur === 'CHF') {
    body = rect(0, 0, 20, 14, '#d52b1e') + rect(8.5, 3.5, 3, 7, '#fff') + rect(6, 5.5, 10, 3, '#fff');
  } else if (cur === 'CAD') {
    body = rect(0, 0, 20, 14, '#fff') + rect(0, 0, 5, 14, '#d52b1e')
      + rect(15, 0, 5, 14, '#d52b1e') + rect(8.5, 5, 3, 4, '#d52b1e');
  } else if (cur === 'AUD') {
    body = rect(0, 0, 20, 14, '#00247d') + rect(0, 0, 9, 7, '#012169')
      + line(0, 0, 9, 7, '#fff', 0.8) + line(9, 0, 0, 7, '#fff', 0.8)
      + circle(14, 10, 0.7, '#fff') + circle(6, 11, 0.6, '#fff') + circle(16, 4, 0.5, '#fff');
  } else if (cur === 'INR') {
    body = rect(0, 0, 20, 4.67, '#FF9933') + rect(0, 4.67, 20, 4.66, '#fff') + rect(0, 9.33, 20, 4.67, '#138808')
      + '<circle cx="10" cy="7" r="2.1" fill="none" stroke="#000080" stroke-width="0.5"/>'
      + circle(10, 7, 0.5, '#000080');
    for (let i = 0; i < 8; i += 1) {
      const a = (Math.PI / 180) * (i * 45);
      body += line(10 + 0.7 * Math.cos(a), 7 + 0.7 * Math.sin(a),
        10 + 1.9 * Math.cos(a), 7 + 1.9 * Math.sin(a), '#000080', 0.25);
    }
  } else {
    body = rect(0, 0, 20, 14, '#64748b');
  }

  return `<svg viewBox="0 0 20 14" width="${FLAG_SIZE.width}" height="${FLAG_SIZE.height}" `
    + `class="cf-cur-flag" aria-hidden="true">${body}</svg>`;
}

export interface CurrencyChipOptions {
  /** Decimal places and symbol come from the currency's own record, never a constant. */
  currency?: MoneyCurrency;
  /** Extra classes, e.g. to mute a chip that is out of scope. */
  className?: string;
  /**
   * A short label notched into the chip's own top border — the wallet a leg
   * belongs to, or the mark that says a figure is a total.
   *
   * The register used to put these beside the chip: a wallet in a column of
   * its own, 138px of track that was mostly whitespace, and a sum mark
   * inside the chip ahead of the flag. Both are properties OF the figure, so
   * the notch is where they belong — the same notch every field on this page
   * already wears, and horizontal room is the thing a ledger row never has.
   *
   * It costs about seven pixels of height per chip and gives back a column.
   *
   * **Text, not markup**, escaped like everything else. The chip is on every
   * page in this app and a component that accepts HTML from its callers has
   * as many layouts as it has callers.
   *
   * Decorative unless `notchSays` is given: a wallet name is worth hearing,
   * `∑∑` is not, and the caller knows which it is passing.
   */
  notch?: string;
  /** What the notch is called when it is read aloud. Omit for a mark that is not a word. */
  notchSays?: string;
}

/**
 * The currency marker on its own — flag and code, no amount.
 *
 * For labelling a field rather than reporting a figure: "Amount 🇭🇺 HUF"
 * says what you are about to type in without pretending to know how much.
 * Built from the same pieces as `currencyChipHtml` so the two cannot drift
 * into looking like different things.
 *
 * The code is announced as a word, because the flag is decorative and the
 * code itself renders at 8.5px.
 */
export function currencyMarkHtml(code: string): string {
  return `<span class="cf-cur-chip" data-cur="${escapeHtml(code)}" role="text" `
    + `aria-label="${escapeHtml(`in ${code}`)}">`
    + `<span class="cf-cur-mark">${flagSvg(code)}<span class="cf-cur-code" aria-hidden="true">${escapeHtml(code)}</span></span>`
    + '</span>';
}

/** `font-size` of `.cf-cur-chip__notch`, in rem. The one number shared with the stylesheet. */
const NOTCH_REM = 0.62;

const notchWidths = new Map<string, number>();
let notchPen: CanvasRenderingContext2D | null | undefined;

function notchFontSize(): number {
  if (typeof document === 'undefined') return 16 * NOTCH_REM;
  const root = parseFloat(getComputedStyle(document.documentElement).fontSize);
  return (root > 0 ? root : 16) * NOTCH_REM;
}

/*
 * The font is read off a real `.cf-cur-chip__notch` rather than assembled
 * from what this file thinks the rule says. The first version asked the root
 * element, which is not where this app sets its family, and measured every
 * label about 18% narrow — a gap that clips the last letter rather than one
 * that is plainly wrong, so it would have read as a rendering glitch.
 */
function pen(): CanvasRenderingContext2D | null {
  if (notchPen !== undefined) return notchPen;
  notchPen = null;
  if (typeof document !== 'undefined' && document.body) {
    const sample = document.createElement('span');
    sample.className = 'cf-cur-chip__notch';
    sample.style.position = 'absolute';
    sample.style.visibility = 'hidden';
    try {
      document.body.appendChild(sample);
      const ctx = document.createElement('canvas').getContext('2d');
      if (ctx) {
        const style = getComputedStyle(sample);
        const size = parseFloat(style.fontSize) || notchFontSize();
        ctx.font = `${style.fontWeight} ${size}px ${style.fontFamily}`;
        notchPen = ctx;
      }
    } catch {
      notchPen = null;
    } finally {
      sample.remove();
    }
  }
  return notchPen;
}

/**
 * How wide the notch text will be, so the stylesheet can cut a gap that size.
 *
 * A canvas measures the string against the same font without touching the
 * document, so 573 chips in one register cost no layout and — a log having a
 * handful of distinct wallet names — a handful of measurements.
 *
 * Without a canvas (a server render, a test outside a DOM) it falls back to a
 * per-character estimate deliberately on the generous side: a gap wider than
 * its label is a notch, a gap narrower than its label is the rim drawn
 * through the last letter.
 */
export function notchTextWidth(text: string): number {
  const known = notchWidths.get(text);
  if (known !== undefined) return known;
  const ctx = pen();
  const width = ctx ? ctx.measureText(text).width : [...text].length * notchFontSize() * 0.7;
  notchWidths.set(text, width);
  return width;
}

/**
 * One signed amount, with its currency.
 *
 * The accessible name carries the code as a word — "469.00 HUF" — because the
 * flag is decorative and the code is rendered at 8.5px. A screen reader
 * hearing only the number would get exactly the ambiguity this removes
 * visually.
 */
export function currencyChipHtml(
  amount: string | number, code: string, options: CurrencyChipOptions = {},
): string {
  const value = Number(amount) || 0;
  const text = formatNum(amount, options.currency ?? { fraction: 2, code });
  const body =
    `<span class="cf-cur-mark">${flagSvg(code)}<span class="cf-cur-code" aria-hidden="true">${escapeHtml(code)}</span></span>`
    + `<span class="cf-cur-amt${value < 0 ? ' is-negative' : ''}" aria-hidden="true">${escapeHtml(text)}</span>`;
  const says = `${text} ${code}${options.notchSays ? `, ${options.notchSays}` : ''}`;
  const attrs = `class="cf-cur-chip${options.className ? ` ${options.className}` : ''}%EXTRA%" `
    + `data-cur="${escapeHtml(code)}" data-amt="${escapeHtml(String(value))}" `
    + `role="text" aria-label="${escapeHtml(says)}"`;

  if (!options.notch) return `<span ${attrs.replace('%EXTRA%', '')}>${body}</span>`;

  /*
   * The notch is a label laid OVER the rim, and the gap it sits in is cut
   * out of the rim itself.
   *
   * This was a `<fieldset>` and a `<legend>` first, for the reason
   * `notchedFieldHtml` still uses them: a browser already draws that pair
   * with the label interrupting the border, and painting a label over a
   * border instead wants a backing plate, which wants the colour behind it
   * to be known — and a register row can be hovered, current, or railed for
   * a shortfall.
   *
   * What that gave up is the glass rim. A chip's rim is a gradient, so it is
   * not a `border` at all: it is a ring masked out of `::after`, which covers
   * the whole box and knows nothing about a legend — it drew a second,
   * unbroken border inside the notched one. Cutting the gap out of that mask
   * instead needs no plate and so needs no backing colour, which is the
   * objection above answered rather than worked around. It needs one number
   * CSS cannot work out, the width of the text, and that is what
   * `--cf-notch-w` carries.
   */
  return `<span ${attrs.replace('%EXTRA%', ' cf-cur-chip--notch')} `
    + `style="--cf-notch-w:${notchTextWidth(options.notch).toFixed(2)}px">`
    + `<span class="cf-cur-chip__notch" aria-hidden="true">${escapeHtml(options.notch)}</span>`
    + `${body}</span>`;
}
